import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { trackEndpointAccess, trackEndpointResponseTime } from "src/utils/analyticsCapFirebase";
import { fetchWithCache } from "src/utils/cacheUtils";
import { resolveUnsyncedPocIntegrationName } from "src/constants/unsyncedLeague";

interface ErrorResponse {
  detail?: string;
  message?: string;
}

// export type RankingsFilter = "all" | "current" | "rookies";

export const getAllRankings = async (
  authId,
  userDataPlatform,
  leagueIdPlatform,
  teamName,
  pocTokens,
  accessAuthToken,
  // rankingsFilter: RankingsFilter = "all",
  signal?: AbortSignal,
  yahooToken?: string,
  forceRefresh = false,
  calculatorSettings?: any,
  /** Needed to safely map null → "un_synced" only for Un-Synced */
  platformId?: string | null,
): Promise<any> => {
  try {
    const startedAt = Date.now();
    // const url = `${process.env.REACT_APP_BACKEND_URL}/functions/all_rankings?filter=${rankingsFilter}`;
    const url = `${process.env.REACT_APP_BACKEND_URL}/functions/all_rankings`;

    const cacheKey = `allRankings-${leagueIdPlatform}-${userDataPlatform.user_id}`;

    const requestData: any = {
      league_integration: {
        leagueToken: userDataPlatform.integration_name === "yahoo" ? yahooToken : userDataPlatform.token,
        pocToken: pocTokens,
        user_id: userDataPlatform.user_id,
        league_id: leagueIdPlatform,
        // Un-Synced: POC enum requires "un_synced" (guarded by platformId)
        integration_name: resolveUnsyncedPocIntegrationName(userDataPlatform.integration_name, platformId),
        force_refresh: forceRefresh,
      },
      sport: "NFL",
      // rankings_filter: rankingsFilter,
      ...(calculatorSettings && { calculator_settings: calculatorSettings }),
    };

    // If the platform is ESPN, add the specific fields it requires
    if (userDataPlatform.integration_name === "espn") {
      requestData.league_integration.swid = userDataPlatform.swid;
      requestData.league_integration.espn_s2 = userDataPlatform.s2;
    }

    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuthToken}`,
        "X-Skip-Cache": "true",
      },
      body: JSON.stringify(requestData),
      signal,
      credentials: "include",
    };

    const { data, fromCache, ok } = await fetchWithCache(url, options, leagueIdPlatform, userDataPlatform.user_id, authId, forceRefresh);

    // --- TRACK EVENT ON REAL EXECUTION (NOT CACHE) ---
    if (!fromCache && ok) {
      void trackEndpointAccess(authId, userDataPlatform.integration_name, leagueIdPlatform, teamName, "all_rankings");
      void trackEndpointResponseTime(authId, "all_rankings", (Date.now() - startedAt) / 1000, userDataPlatform.integration_name, leagueIdPlatform);
    }

    if (!ok) {
      const errorData = data as ErrorResponse & { statusCode?: number };
      // Detect rate limit via response body (fetchWithCache doesn't expose HTTP status)
      if (errorData?.statusCode === 429 || errorData?.message === "TOO_MANY_REQUESTS") {
        const err = new Error("TOO_MANY_REQUESTS") as any;
        err.isRateLimit = true;
        err.retryAfter = (errorData as any)?.retryAfter ?? 60;
        throw err;
      }
      throw new Error(errorData.detail || errorData.message);
    }

    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Request was aborted");
      return;
    }
    // Rate limit: show specific message without exposing the raw error code
    if ((error as any).isRateLimit) {
      showErrorToast("You're making too many requests. Please slow down and try again in a moment.");
      throw error;
    }
    console.error("Error in getAllRankings:", error);
    showErrorToast(error.message);
    throw error;
  }
};